import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICliente, IQueryParam } from 'modelos/src';
import {
  ClientesService,
  IConteoEntidadCliente,
} from '../../../../../../auxiliares/servicios/http/clientes.service';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { LoginService } from '../../../../../login/login.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';

export type TipoOperacionCliente =
  | 'cambiarPadre'
  | 'moverEntidades'
  | 'eliminarConMover';

@Component({
  selector: 'app-cambiar-padre-dialog',
  templateUrl: './cambiar-padre-dialog.component.html',
  styleUrls: ['./cambiar-padre-dialog.component.scss'],
  standalone: false,
})
export class CambiarPadreDialogComponent implements OnInit {
  matDialogRef =
    inject<MatDialogRef<CambiarPadreDialogComponent>>(MatDialogRef);
  data = inject<{
    idCliente: string;
    nombreCliente: string;
    tipoOperacion: TipoOperacionCliente;
  }>(MAT_DIALOG_DATA);
  private clientesService = inject(ClientesService);
  private helper = inject(HelperService);
  private lista2Service = inject(Lista2Service);
  private cdr = inject(ChangeDetectorRef);

  public title: string;
  public message: string;
  public btnOkText: string;
  public btnCancelText: string;
  public idCliente: string;
  public nombreCliente: string;
  public tipoOperacion: TipoOperacionCliente;

  public clientes: ICliente[] = [];
  public clientesFiltrados: ICliente[] = [];
  public clienteSeleccionado?: ICliente;
  public searchText: string = '';
  public loading: boolean = false;

  // Selección de qué mover (solo para 'moverEntidades'). El backend acepta la
  // lista vacía como "mover todo", pero acá se exige elegir al menos una para
  // que no se mueva todo por accidente.
  public entidades: IConteoEntidadCliente[] = [];
  public entidadesSeleccionadas = new Set<string>();
  public cargandoEntidades = false;
  public errorEntidades?: string;

  constructor() {
    this.idCliente = this.data.idCliente;
    this.nombreCliente = this.data.nombreCliente;
    this.tipoOperacion = this.data.tipoOperacion || 'cambiarPadre';

    // Configurar textos según el tipo de operación
    if (this.tipoOperacion === 'cambiarPadre') {
      this.title = 'Cambiar Cliente Padre';
      this.message = `Seleccioná el nuevo cliente padre para los hijos de "${this.nombreCliente}"`;
      this.btnOkText = 'Cambiar Padre';
    } else if (this.tipoOperacion === 'moverEntidades') {
      this.title = 'Mover Entidades a Otro Cliente';
      this.message = `Seleccioná el cliente destino y qué entidades de "${this.nombreCliente}" mover`;
      this.btnOkText = 'Mover Entidades';
    } else if (this.tipoOperacion === 'eliminarConMover') {
      this.title = 'Eliminar Cliente';
      this.message = `Antes de eliminar "${this.nombreCliente}", seleccioná opcionalmente un cliente destino para mover sus entidades. Si no seleccionás ninguno, las entidades se eliminarán junto con el cliente.`;
      this.btnOkText = 'Eliminar Cliente';
    }

    this.btnCancelText = 'Cancelar';
  }

  async ngOnInit() {
    await this.cargarClientes();
    if (this.tipoOperacion === 'moverEntidades') {
      await this.cargarEntidades();
    }
  }

  public async cargarClientes(): Promise<void> {
    const query: IQueryParam = {
      select: 'nombre numeroCliente',
      unlimited: true,
      sort: 'nombre',
      includeChildren: true,
    };
    this.lista2Service.clientes(query).subscribe(async (data) => {
      this.clientes = data.datos;
      this.clientesFiltrados = this.clientesDestinoPosibles();
    });
  }

  /**
   * Trae qué tiene el cliente y deja preseleccionado todo lo que tenga algo.
   * Los tipos con cantidad 0 no se ofrecen: no hay nada que mover.
   */
  public async cargarEntidades(): Promise<void> {
    this.cargandoEntidades = true;
    this.errorEntidades = undefined;
    try {
      const conteo = await this.clientesService.conteoEntidades(this.idCliente);
      this.entidades = (conteo || [])
        .filter((entidad) => entidad.cantidad > 0 || entidad.error)
        .sort((a, b) => a.label.localeCompare(b.label));
      this.entidadesSeleccionadas = new Set(
        this.entidades.map((entidad) => entidad.clave),
      );
    } catch (error) {
      console.error('Error trayendo el conteo de entidades:', error);
      this.errorEntidades =
        'No se pudo traer el detalle de entidades del cliente';
      this.entidades = [];
      this.entidadesSeleccionadas.clear();
    } finally {
      this.cargandoEntidades = false;
      this.cdr.detectChanges();
    }
  }

  public estaSeleccionada(clave: string): boolean {
    return this.entidadesSeleccionadas.has(clave);
  }

  public toggleEntidad(clave: string, seleccionada: boolean) {
    if (seleccionada) {
      this.entidadesSeleccionadas.add(clave);
    } else {
      this.entidadesSeleccionadas.delete(clave);
    }
  }

  public seleccionarTodas() {
    this.entidadesSeleccionadas = new Set(
      this.entidades.map((entidad) => entidad.clave),
    );
  }

  public seleccionarNinguna() {
    this.entidadesSeleccionadas = new Set();
  }

  public get totalAMover(): number {
    return this.entidades
      .filter((entidad) => this.entidadesSeleccionadas.has(entidad.clave))
      .reduce((total, entidad) => total + entidad.cantidad, 0);
  }

  public filtrarClientes() {
    const posibles = this.clientesDestinoPosibles();
    if (!this.searchText || this.searchText.trim() === '') {
      this.clientesFiltrados = posibles;
      return;
    }

    const search = this.searchText.toLowerCase().trim();
    this.clientesFiltrados = posibles.filter((cliente) =>
      cliente.nombre.toLowerCase().includes(search),
    );
  }

  // El propio cliente nunca es un destino válido (ni de sus entidades ni de sus
  // hijos), así que no se lista.
  private clientesDestinoPosibles(): ICliente[] {
    return this.clientes.filter((cliente) => cliente._id !== this.idCliente);
  }

  public seleccionarCliente(cliente: ICliente) {
    this.clienteSeleccionado = cliente;
    // Forzar detección de cambios para actualizar la UI
    this.cdr.detectChanges();
  }

  public get puedeAceptar(): boolean {
    if (this.loading) return false;
    if (this.tipoOperacion === 'eliminarConMover') return true;
    if (!this.clienteSeleccionado) return false;
    if (this.tipoOperacion === 'moverEntidades') {
      return this.entidadesSeleccionadas.size > 0;
    }
    return true;
  }

  public async aceptar() {
    // Para eliminación, la selección de cliente es opcional
    if (
      this.tipoOperacion !== 'eliminarConMover' &&
      !this.clienteSeleccionado
    ) {
      const errorMsg =
        this.tipoOperacion === 'cambiarPadre'
          ? 'Tenés que seleccionar un cliente padre'
          : 'Tenés que seleccionar un cliente destino';
      this.helper.notifError(errorMsg);
      return;
    }
    if (
      this.tipoOperacion === 'moverEntidades' &&
      !this.entidadesSeleccionadas.size
    ) {
      this.helper.notifError(
        'Tenés que seleccionar al menos una entidad a mover',
      );
      return;
    }

    const cliente = this.clientes.find((c) => c._id === this.idCliente);

    this.loading = true;
    try {
      if (this.tipoOperacion === 'cambiarPadre') {
        await this.clientesService.cambiarHijosDePadre(
          this.idCliente,
          this.clienteSeleccionado!._id,
        );
        this.helper.notifSuccess('Cliente padre cambiado exitosamente');
      } else if (this.tipoOperacion === 'moverEntidades') {
        await this.clientesService.moverEntidadesAOtroCliente(
          this.idCliente,
          this.clienteSeleccionado!._id,
          [...this.entidadesSeleccionadas],
        );
        this.helper.notifSuccess('Entidades movidas exitosamente');
      } else if (this.tipoOperacion === 'eliminarConMover') {
        if (this.clienteSeleccionado) {
          await this.clientesService.delete(
            this.idCliente,
            this.clienteSeleccionado._id,
          );
          this.helper.notifSuccess(
            'Cliente eliminado y entidades movidas exitosamente',
          );
        } else {
          await this.clientesService.delete(this.idCliente);
          this.helper.notifSuccess('Cliente eliminado exitosamente');
        }
      }
      this.matDialogRef.close(true);
    } catch (error) {
      console.error(`Error en operación ${this.tipoOperacion}:`, error);
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }

  public cancelar() {
    this.matDialogRef.close(false);
  }
}
